import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type CSSProperties,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type RadioOption = {
  value: string;
  label: string;
};

type FilterControlContextValue = {
  controlValues: Record<string, string | boolean>;
  onChange: (value: string | boolean, key: string) => void;
  context: string | undefined;
  tooltipOpen: Readonly<Record<string, boolean>>;
  toggleTooltip: (key: string) => void;
};

type FilterControlRootProps = {
  controlValues: Record<string, string | boolean>;
  onChange: (value: string | boolean, key: string) => void;
  context?: string;
  children: ReactNode;
  dataId?: string;
};

type FilterControlContainerProps = {
  children: ReactNode;
};

type RadioGroupControlProps = {
  controlKey: string;
  label: string;
  ariaLabel: string;
  options: RadioOption[];
  tooltip?: string;
};

type CheckboxControlProps = {
  controlKey: string;
  label: string;
};

const filterControlStyles = {
  listItem: {
    backgroundColor: '#fff',
    padding: '1rem 1rem 0.25rem',
  } as CSSProperties,
  fieldset: {
    all: 'unset',
  } as CSSProperties,
  legend: {},
  group: {},
  options: {
    display: 'flex',
  },
  tooltip: {},
  tooltipButton: {},
};

const FilterControlContext = createContext<FilterControlContextValue | undefined>(undefined);

const useFilterControlContext = (): FilterControlContextValue => {
  const context = useContext(FilterControlContext);

  if (context === undefined) {
    throw new Error(
      'FilterControl subcomponents must be used within FilterControl.Root. ' +
        'Ensure your component is wrapped in <FilterControl.Root>.',
    );
  }

  return context;
};

const FilterControlRoot = ({
  controlValues,
  onChange,
  context,
  children,
  dataId,
}: FilterControlRootProps) => {
  const [tooltipOpen, setTooltipOpen] = useState<Record<string, boolean>>({});

  const toggleTooltip = useCallback((key: string): void => {
    setTooltipOpen(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const contextValue = useMemo<FilterControlContextValue>(
    () => ({
      controlValues,
      onChange,
      context,
      tooltipOpen,
      toggleTooltip,
    }),
    [controlValues, onChange, context, tooltipOpen, toggleTooltip],
  );

  return (
    <FilterControlContext.Provider value={contextValue}>
      <Box data-id={dataId ?? 'filter-control'}>{children}</Box>
    </FilterControlContext.Provider>
  );
};

const FilterControlContainer = ({ children }: FilterControlContainerProps) => {
  const handleClick = useCallback((e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <li style={filterControlStyles.listItem} data-id='filter-control-container'>
      <FormControl onClick={handleClick} data-id='filter-control-form-control'>
        {children}
      </FormControl>
    </li>
  );
};

const RadioGroupControl = ({
  controlKey,
  label,
  ariaLabel,
  options,
  tooltip,
}: RadioGroupControlProps) => {
  const { controlValues, onChange, context, tooltipOpen, toggleTooltip } =
    useFilterControlContext();

  const handleOptionClick = useCallback(
    (value: string): void => {
      onChange(value, controlKey);
    },
    [onChange, controlKey],
  );

  const handleTooltipToggle = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      toggleTooltip(controlKey);
    },
    [toggleTooltip, controlKey],
  );

  const fieldsetId = useMemo(
    () => `filter-control-radio-${context ?? 'default'}-${controlKey}`,
    [context, controlKey],
  );

  const currentValue = controlValues[controlKey];
  const isTooltipOpen = tooltipOpen[controlKey] ?? false;

  return (
    <fieldset
      id={fieldsetId}
      style={filterControlStyles.fieldset}
      data-id='filter-control-radio-group'
    >
      <Typography
        variant='body2'
        component='legend'
        sx={filterControlStyles.legend}
        data-id='filter-control-radio-label'
      >
        {label.toUpperCase()}
      </Typography>
      <RadioGroup
        sx={filterControlStyles.group}
        data-id='filter-control-radio-group-wrapper'
        aria-label={ariaLabel}
        value={currentValue}
      >
        <div
          style={filterControlStyles.options as CSSProperties}
          data-id='filter-control-radio-options'
        >
          {options.map(option => (
            <FormControlLabel
              key={`${controlKey}-${option.value}`}
              value={option.value}
              control={<Radio size='small' />}
              label={option.label}
              onClick={() => handleOptionClick(option.value)}
              data-id='filter-control-radio-option'
            />
          ))}
        </div>
      </RadioGroup>
      {tooltip && (
        <Tooltip
          title={<div style={filterControlStyles.tooltip as CSSProperties}>{tooltip}</div>}
          open={isTooltipOpen}
          placement='bottom-start'
          disableHoverListener
          disableFocusListener
          disableTouchListener
          data-id='filter-control-tooltip'
        >
          <IconButton
            size='small'
            sx={filterControlStyles.tooltipButton}
            onClick={handleTooltipToggle}
            aria-label={`Information about ${label}`}
            aria-expanded={isTooltipOpen}
            data-id='filter-control-tooltip-button'
          >
            <InfoOutlinedIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      )}
    </fieldset>
  );
};

const CheckboxControl = ({ controlKey, label }: CheckboxControlProps) => {
  const { controlValues, onChange, context } = useFilterControlContext();

  const handleClick = useCallback((): void => {
    const currentValue = controlValues[controlKey];
    const newValue = !currentValue;
    onChange(newValue, controlKey);
  }, [controlValues, onChange, controlKey]);

  const fieldsetId = useMemo(
    () => `filter-control-checkbox-${context ?? 'default'}-${controlKey}`,
    [context, controlKey],
  );

  const isChecked = Boolean(controlValues[controlKey]);

  return (
    <Box
      component='fieldset'
      id={fieldsetId}
      style={filterControlStyles.fieldset as CSSProperties}
      data-id='filter-control-checkbox-group'
    >
      <FormControlLabel
        onClick={handleClick}
        control={<Checkbox size='small' checked={isChecked} />}
        label={label}
        data-id='filter-control-checkbox-option'
      />
    </Box>
  );
};

const FilterControl = Object.assign(FilterControlRoot, {
  Root: FilterControlRoot,
  Container: FilterControlContainer,
  RadioGroup: RadioGroupControl,
  Checkbox: CheckboxControl,
});

export { FilterControl };
